import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  IconButton,
  InputGroup,
  InputLeftElement,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  SimpleGrid,
  List,
  ListItem,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { saveSearchToLocalStorage } from "../../utils/Helper";
import { ChevronDownIcon, SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, TabList, Tab } from "@chakra-ui/react";
import { setListingQueryParams } from "../../redux/reducers/propertyReducer";
import { propertyType, locationOption } from "../../utils/constant";
import { useNavigate, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

// Helper function to format number without currency symbol
const formatNumber = (value) => {
  if (!value && value !== 0) return "";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const initialStateData = {
  title_en: "",
  propertyType: "",
  propertyPurpose: "",
  bedroom: "",
  bathroom: "",
  minSize: "",
  maxSize: "",
  minPrice: "",
  maxPrice: "",
  community: "",
  completion_status: "",
};

const SearchBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const searchRef = useRef(null);
  const { listingQueryParams } = useSelector((state) => state?.property);
  const isMobile = useBreakpointValue({ base: true, md: false });


  const reversePropertyPurposeMapping = React.useMemo(
    () => ({
      rent: "Rent",
      sale: "Buy",
      commercial: "Commercial",
    }),
    []
  );

  useEffect(() => {
    if (location.pathname === "/properties") {
      setStateData({
        title_en: listingQueryParams?.title_en || "",
        propertyType: listingQueryParams?.property_type || "",
        bedroom: listingQueryParams?.bedroom || "",
        bathroom: listingQueryParams?.bathroom || "",
        minSize: listingQueryParams?.minSize || "",
        maxSize: listingQueryParams?.maxSize || "",
        minPrice: listingQueryParams?.minPrice || "",
        maxPrice: listingQueryParams?.maxPrice || "",
        community: listingQueryParams?.community || "",
        completion_status: listingQueryParams?.completion_status || "",
      });
      if (listingQueryParams?.property_purpose) {
        setSelectedTab(reversePropertyPurposeMapping[listingQueryParams.property_purpose]);
      }
    }
  }, [listingQueryParams, location.pathname, reversePropertyPurposeMapping]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const propertyPurposeMapping = {
    Rent: "rent",
    Buy: "sale",
    Commercial: "commercial",
  };

  const [stateData, setStateData] = useState(initialStateData);
  const [selectedTab, setSelectedTab] = useState("Rent");
  const [filteredLocations, setFilteredLocations] = useState(locationOption);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMinMenuOpen, setIsMinMenuOpen] = useState(false);
  const [isMaxMenuOpen, setIsMaxMenuOpen] = useState(false);
  const [isMinPriceMenuOpen, setIsMinPriceMenuOpen] = useState(false);
  const [isMaxPriceMenuOpen, setIsMaxPriceMenuOpen] = useState(false);

  // Placeholder animation state
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typeSpeed, setTypeSpeed] = useState(100);

  const phrases = React.useMemo(() => [
    intl.formatMessage({ id: "searchBar.placeholder_city", defaultMessage: "Search by City..." }),
    intl.formatMessage({ id: "searchBar.placeholder_community", defaultMessage: "Search by Community..." }),
    intl.formatMessage({ id: "searchBar.placeholder_building", defaultMessage: "Search by Building..." }),
  ], [intl]);

  useEffect(() => {
    setAnimatedPlaceholder("");
    setCurrentPhraseIndex(0);
    setIsDeleting(false);
  }, [phrases]);

  useEffect(() => {
    if (stateData?.community) {
      setAnimatedPlaceholder("");
      return;
    }

    const currentPhrase = phrases[currentPhraseIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setAnimatedPlaceholder(currentPhrase.substring(0, animatedPlaceholder.length + 1));
        if (animatedPlaceholder.length + 1 === currentPhrase.length) {
          setTimeout(() => setIsDeleting(true), 1500); // Pause at end
        }
      } else {
        setAnimatedPlaceholder(currentPhrase.substring(0, animatedPlaceholder.length - 1));
        if (animatedPlaceholder.length === 0) {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? 30 : 60);

    return () => clearTimeout(timeout);
  }, [animatedPlaceholder, isDeleting, currentPhraseIndex, stateData?.community, phrases]);

  const areaOptions = [
    "500",
    "1000",
    "1500",
    "2000",
    "2500",
    "3000",
    "3500",
    "4000",
    "4500",
    "5000",
  ];

  const priceOptions = [
    "50000",
    "100000",
    "200000",
    "300000",
    "400000",
    "500000",
    "750000",
    "1000000",
    "1500000",
    "2000000",
    "3000000",
    "4000000",
    "5000000",
    "7500000",
    "10000000",
  ];

  const bedroomOptions = [
    { label: "Studio", value: "studio" },
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
    { label: "5", value: "5" },
    { label: "6", value: "6" },
    { label: "7+", value: "7+" },
  ];

  const bathroomOptions = [
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
    { label: "5", value: "5" },
    { label: "6", value: "6" },
    { label: "7+", value: "7+" },
  ];

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get("tab");

    if (tab) {
      if (reversePropertyPurposeMapping[tab]) {
        setSelectedTab(reversePropertyPurposeMapping[tab]);
      }
    }
  }, [location.search, reversePropertyPurposeMapping]);

  const handleTabChange = (tabName) => {
    setSelectedTab(tabName);
    setStateData(initialStateData);

    let updatedState = {};
    if (tabName === "Buy") {
      updatedState = {
        completion_status: "off_plan",
        property_purpose: propertyPurposeMapping[tabName],
      };
    } else {
      updatedState = {
        property_purpose: propertyPurposeMapping[tabName],
        completion_status: null,
      };
    }

    dispatch(
      setListingQueryParams({
        take: 6,
        skip: 0,
        currentPage: 1,
        ...updatedState,
        selectedTab: tabName,
      })
    );
  };

  const handlePropertyTypeChange = (key) => {
    const selectedPropertyType = propertyType.find(
      (type) => type.key === key
    )?.value;

    setStateData((prev) => {
      const isOfficeSpace = selectedPropertyType === "OF";
      return {
        ...prev,
        propertyType: selectedPropertyType,
        ...(isOfficeSpace ? { bedroom: "", bathroom: "" } : {}),
      };
    });
  };

  const handleBedroomChange = (value) => {
    setStateData((prev) => ({ ...prev, bedroom: value }));
  };

  const handleBathroomChange = (value) => {
    setStateData((prev) => ({ ...prev, bathroom: value }));
  };

  const handleMinAreaChange = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent.target.value;
    setStateData((prev) => ({ ...prev, minSize: value }));
  };

  const handleMaxAreaChange = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent.target.value;
    setStateData((prev) => ({ ...prev, maxSize: value }));
  };

  const handleMinPriceChange = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent.replace(/\D/g, "") // Remove non-digit characters
        : valueOrEvent.target.value.replace(/\D/g, "");
    setStateData((prev) => {
      // If max price exists and is less than new min price, update max price to match
      const updates = { minPrice: value };
      if (value && prev.maxPrice && Number(value) > Number(prev.maxPrice)) {
        updates.maxPrice = value;
      }
      return { ...prev, ...updates };
    });
  };

  const handleMaxPriceChange = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent.replace(/\D/g, "") // Remove non-digit characters
        : valueOrEvent.target.value.replace(/\D/g, "");
    setStateData((prev) => {
      // If min price exists and is greater than new max price, update min price to match
      const updates = { maxPrice: value };
      if (value && prev.minPrice && Number(value) < Number(prev.minPrice)) {
        updates.minPrice = value;
      }
      return { ...prev, ...updates };
    });
  };

  const renderPriceRangeText = () => {
    const aed = intl.formatMessage({ id: "searchBar.aed", defaultMessage: "AED" });
    const upTo = intl.formatMessage({ id: "searchBar.up_to", defaultMessage: "Up to" });
    const plus = intl.formatMessage({ id: "searchBar.plus", defaultMessage: "+" });

    if (stateData?.minPrice && stateData?.maxPrice) {
      return `${aed} ${formatNumber(stateData.minPrice)} - ${aed} ${formatNumber(
        stateData.maxPrice
      )}`;
    } else if (stateData?.minPrice) {
      return `${aed} ${formatNumber(stateData.minPrice)} ${plus}`;
    } else if (stateData?.maxPrice) {
      return `${upTo} ${aed} ${formatNumber(stateData.maxPrice)}`;
    }
    return intl.formatMessage({
      id: "searchBar.price_range",
      defaultMessage: "Price Range",
    });
  };

  const renderBedsBathsText = () => {
    const beds = stateData.bedroom;
    const baths = stateData.bathroom;

    if (beds && baths) {
      const bedsLabel = beds === "studio" ? "Studio" : `${beds} Beds`;
      return `${bedsLabel}, ${baths} Baths`;
    } else if (beds) {
      return beds === "studio" ? "Studio" : `${beds} Beds`;
    } else if (baths) {
      return `${baths} Baths`;
    }
    return intl.formatMessage({
      id: "searchBar.beds_baths",
      defaultMessage: "Beds & Baths",
    });
  };

  const handleCommunityChange = (e) => {
    const value = e.target.value;
    setStateData((prev) => ({ ...prev, community: value }));

    const filtered = locationOption.filter((option) =>
      option.community.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredLocations(filtered);
    setShowSuggestions(true);

    if (value === "") {
      const cleanParams = getCleanQueryParams();
      dispatch(setListingQueryParams({ ...cleanParams, community: null, currentPage: 1 }));
    }
  };

  const commercialPropertyTypes = propertyType.filter((type) =>
    [
      "property_type.office_space",
      "property_type.show_room",
      "property_type.short_term_hotel_apartment",
    ].includes(type?.key)
  );

  const otherPropertyTypes = propertyType.filter((type) =>
    [
      "property_type.apartment",
      "property_type.duplex",
      "property_type.penthouse",
      "property_type.townhouse",
      "property_type.villa",
    ].includes(type?.key)
  );

  const propertyTypesToShow =
    selectedTab === "Commercial" ? commercialPropertyTypes : otherPropertyTypes;

  useEffect(() => {
    if (location?.state) {
      setStateData(location.state);
      if (location.state.selectedTab) {
        setSelectedTab(location.state.selectedTab);
      }
    }
  }, [location?.state]);

  const getCleanQueryParams = () => {
    const queryParams = {};
    if (stateData?.propertyType) {
      queryParams.property_type = stateData.propertyType;
    }

    if (stateData?.bedroom) {
      queryParams.bedroom = stateData.bedroom;
      if (stateData.bedroom === "studio") {
        queryParams.title_en = "studio";
      }
    }

    if (stateData?.bathroom) {
      queryParams.bathroom = stateData.bathroom;
    }

    if (stateData?.minSize) {
      queryParams.minSize = stateData.minSize;
    }
    if (stateData?.maxSize) {
      queryParams.maxSize = stateData.maxSize;
    }
    if (stateData?.minPrice) {
      queryParams.minPrice = stateData.minPrice;
    }
    if (stateData?.maxPrice) {
      queryParams.maxPrice = stateData.maxPrice;
    }
    if (stateData?.community) {
      queryParams.community = stateData.community;
    }

    if (selectedTab === "Buy") {
      queryParams.completion_status = "off_plan";
    }

    queryParams.property_purpose = propertyPurposeMapping[selectedTab];
    queryParams.skip = 0;
    queryParams.take = 6;
    queryParams.current = 1;

    return queryParams;
  };

  const searchHandler = () => {
    const cleanQueryParams = getCleanQueryParams();

    dispatch(setListingQueryParams({ ...cleanQueryParams, currentPage: 1 }));
    saveSearchToLocalStorage(cleanQueryParams);

    const tabParam = propertyPurposeMapping[selectedTab];
    navigate(`/properties?tab=${tabParam}`, {
      state: { ...stateData, selectedTab },
    });
  };

  const resetPropertyType = () =>
    setStateData((prev) => ({ ...prev, propertyType: "" }));
  const resetBedroom = () =>
    setStateData((prev) => ({ ...prev, bedroom: "", bathroom: "" }));
  const resetArea = () =>
    setStateData((prev) => ({ ...prev, minSize: "", maxSize: "" }));
  const resetPrice = () =>
    setStateData((prev) => ({ ...prev, minPrice: "", maxPrice: "" }));


  const tabs = [
    { id: "Rent", label: "searchBar.rent" },
    { id: "Buy", label: "searchBar.tab_buy" },
    { id: "Commercial", label: "searchBar.tab_commercial" },
  ];

  return (
    <Box
      width="100%"
      mt={8}
    >
      <Flex
        mb={0}
        overflowX="auto"
        sx={{ '&::-webkit-scrollbar': { display: 'none' } }}
        bg="white"
        display={{ base: "flex", md: "inline-flex" }}
        width={{ base: "100%", md: "auto" }}
        borderTopRadius="20px"
        borderBottom="none"
      >
        {tabs.map((tab, index) => (
          <Box
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            cursor="pointer"
            position="relative"
            px={{ base: 4, sm: 6, md: 10 }}
            py={{ base: 3, sm: 4, md: 6 }}
            transition="all 0.2s"
            borderRight={index < tabs.length - 1 ? "1px solid" : "none"}
            borderColor="gray.100"
            _hover={{ bg: "gray.50" }}
          >
            <Text
              color={selectedTab === tab.id ? "#E5BE54" : "gray.500"}
              fontSize={{ base: "xs", sm: "sm", md: "md" }}
              fontWeight={selectedTab === tab.id ? "700" : "600"}
              whiteSpace="nowrap"
            >
              <FormattedMessage id={tab.label} />
            </Text>
            {selectedTab === tab.id && (
              <Box
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                height="3px"
                bg="#E5BE54"
              />
            )}
          </Box>
        ))}
      </Flex>

      <Box
        bg="white"
        p={{ base: 4, md: 5 }}
        borderBottomRadius="20px"
        borderTopRightRadius={{ base: 0, md: "20px" }}
        borderTopLeftRadius="0"
        boxShadow="0 20px 40px rgba(0,0,0,0.15)"
      >
        <Flex gap={4} direction={{ base: "column", md: "row" }} align="center" width="100%">
          <Flex
            bg="#f7f7f7"
            borderBottomRadius="15px"
            borderTopRightRadius="15px"
            borderTopLeftRadius="0"
            flex={1}
            align="center"
            gap={0}
            direction={{ base: "column", md: "row" }}
            width="100%"
            px={2}
          >
            <InputGroup
              ref={searchRef}
              size="lg"
              bg="transparent"
              flex={2}
            >
              <InputLeftElement
                pointerEvents="none"
                h="full"
                display="flex"
                alignItems="center"
                children={<img src="/icons/search-home.svg" alt="home search icon" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />}
              />
              <Input
                placeholder={animatedPlaceholder}
                variant="unstyled"
                pl={'45px'}
                py={5}
                fontSize="md"
                fontWeight="400"
                color="black"
                value={stateData?.community || ""}
                onChange={handleCommunityChange}
              />
              {stateData?.community && (
                <IconButton
                  aria-label="clear search"
                  icon={<CloseIcon size="xs" color="gray.400" />}
                  size="xs"
                  variant="ghost"
                  position="absolute"
                  right="10px"
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={2}
                  onClick={() => {
                    handleCommunityChange({ target: { value: "" } });
                    setShowSuggestions(false);
                  }}
                  _hover={{ bg: "transparent", color: "gray.600" }}
                />
              )}
            </InputGroup>

            <Box w="1px" h="50px" bg="gray.300" display={{ base: "none", md: "block" }} mx={2} />

            <Menu matchWidth={isMobile}>
              <MenuButton
                as={Button}
                variant="unstyled"
                flex={1}
                height="auto"
                px={4}
                py={5}
                width="100%"
                borderRadius="lg"
                transition="all 0.2s"
                _hover={{ bg: "gray.100" }}
              >
                <Flex align="center" justify="space-between" width="100%">
                  <Text
                    isTruncated
                    fontSize="md"
                    fontWeight="300"
                    color="gray.600"
                    textAlign="left"
                  >
                    {stateData?.propertyType ? (
                      intl.formatMessage({
                        id: propertyTypesToShow.find(t => t.value === stateData.propertyType)?.key || "searchBar.property_type"
                      })
                    ) : intl.formatMessage({ id: "searchBar.property_type", defaultMessage: "Property Type" })}
                  </Text>
                  <ChevronDownIcon color="gray.400" ml={2} />
                </Flex>
              </MenuButton>
              <MenuList borderRadius="12px" boxShadow="xl" zIndex={2000}>
                {propertyTypesToShow?.map((type) => (
                  <MenuItem
                    key={type?.value}
                    onClick={() => handlePropertyTypeChange(type?.key)}
                    color="black"
                  >
                    <FormattedMessage id={type?.key} />
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={resetPropertyType}
                  color="red.500"
                  fontWeight="500"
                  borderTop="1px solid"
                  borderColor="gray.100"
                >
                  {intl.formatMessage({ id: "reset", defaultMessage: "Reset" })}
                </MenuItem>
              </MenuList>
            </Menu>



            {selectedTab !== "Commercial" && (
              <>
                <Box w="1px" h="50px" bg="gray.300" display={{ base: "none", md: "block" }} mx={2} />
                <Menu closeOnSelect={false} matchWidth={isMobile}>
                  {({ onClose }) => (
                    <>
                      <MenuButton
                        as={Button}
                        variant="unstyled"
                        flex={1}
                        height="auto"
                        px={4}
                        py={5}
                        width="100%"
                        borderRadius="lg"
                        transition="all 0.2s"
                        _hover={{ bg: "gray.100" }}
                      >
                        <Flex align="center" justify="space-between" width="100%">
                          <Text isTruncated fontSize="md" fontWeight="300" color="gray.600" textAlign="left">
                            {renderBedsBathsText()}
                          </Text>
                          <ChevronDownIcon color="gray.400" ml={2} />
                        </Flex>
                      </MenuButton>
                      <MenuList p={{ base: 4, md: 6 }} borderRadius="16px" boxShadow="2xl" border="1px solid" borderColor="gray.100" zIndex={2000} minW={{ base: "0", md: "300px" }}>
                        <Flex direction="column" gap={4}>
                          <Box>
                            <Text mb={4} fontSize="md" fontWeight="700" color="black">
                              {intl.formatMessage({ id: "searchBar.bedrooms", defaultMessage: "Bedrooms" })}
                            </Text>
                            <SimpleGrid columns={4} spacing={2}>
                              {bedroomOptions.map((opt) => (
                                <Button
                                  key={opt.value}
                                  onClick={() => {
                                    handleBedroomChange(opt.value);
                                    onClose();
                                  }}
                                  variant="unstyled"
                                  h="40px"
                                  fontSize="sm"
                                  fontWeight={stateData.bedroom === opt.value ? "700" : "400"}
                                  color={stateData.bedroom === opt.value ? "black" : "gray.600"}
                                  _hover={{ bg: "gray.50" }}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {opt.label}
                                </Button>
                              ))}
                            </SimpleGrid>
                          </Box>

                          <Box mt={2}>
                            <Text mb={4} fontSize="md" fontWeight="700" color="black">
                              {intl.formatMessage({ id: "searchBar.bathrooms", defaultMessage: "Bathrooms" })}
                            </Text>
                            <SimpleGrid columns={4} spacing={2}>
                              {bathroomOptions.map((opt) => (
                                <Button
                                  key={opt.value}
                                  onClick={() => {
                                    handleBathroomChange(opt.value);
                                    onClose();
                                  }}
                                  variant="unstyled"
                                  h="40px"
                                  fontSize="sm"
                                  fontWeight={stateData.bathroom === opt.value ? "700" : "400"}
                                  color={stateData.bathroom === opt.value ? "black" : "gray.600"}
                                  _hover={{ bg: "gray.50" }}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {opt.label}
                                </Button>
                              ))}
                            </SimpleGrid>
                          </Box>

                          <Flex mt={2} justify="flex-start">
                            <Button
                              variant="unstyled"
                              fontSize="sm"
                              fontWeight="400"
                              color="black"
                              onClick={() => {
                                resetBedroom();
                                onClose();
                              }}
                              _hover={{ textDecoration: "underline" }}
                            >
                              {intl.formatMessage({ id: "reset", defaultMessage: "Reset" })}
                            </Button>
                          </Flex>
                        </Flex>
                      </MenuList>
                    </>
                  )}
                </Menu>
              </>
            )}

            <Box w="1px" h="50px" bg="gray.300" display={{ base: "none", md: "block" }} mx={2} />

            <Menu matchWidth={isMobile}>
              {({ onClose }) => (
                <>
                  <MenuButton
                    as={Button}
                    variant="unstyled"
                    flex={1}
                    height="auto"
                    px={4}
                    py={5}
                    width="100%"
                    borderRadius="lg"
                    transition="all 0.2s"
                    _hover={{ bg: "gray.100" }}
                  >
                    <Flex align="center" justify="space-between" width="100%">
                      <Text isTruncated fontSize="md" fontWeight="300" color="gray.600" textAlign="left">
                        {renderPriceRangeText()}
                      </Text>
                      <ChevronDownIcon color="gray.400" ml={2} />
                    </Flex>
                  </MenuButton>
                  <MenuList p={{ base: 4, md: 6 }} borderRadius="16px" boxShadow="2xl" border="1px solid" borderColor="gray.100" zIndex={2000} minW={{ base: "0", md: "350px" }}>
                    <Flex direction="column" gap={6}>
                      <Flex align="center" gap={3}>
                        <Box flex={1}>
                          <Text mb={2} fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="1px">
                            {intl.formatMessage({ id: "searchBar.min", defaultMessage: "Min Price" })}
                          </Text>
                          <Input
                            placeholder="0"
                            value={formatNumber(stateData.minPrice)}
                            onChange={handleMinPriceChange}
                            h="45px"
                            borderRadius="12px"
                            fontSize="md"
                            color="black"
                            fontFamily="'Plus Jakarta Sans', sans-serif"
                            _focus={{ borderColor: "#e5be54", boxShadow: "0 0 0 1px #e5be54" }}
                            bg="gray.50"
                          />
                        </Box>
                        <Text mt={6} color="gray.400" fontWeight="300">—</Text>
                        <Box flex={1}>
                          <Text mb={2} fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="1px">
                            {intl.formatMessage({ id: "searchBar.max", defaultMessage: "Max Price" })}
                          </Text>
                          <Input
                            placeholder={intl.formatMessage({ id: "searchBar.any", defaultMessage: "Any" })}
                            value={formatNumber(stateData.maxPrice)}
                            onChange={handleMaxPriceChange}
                            h="45px"
                            borderRadius="12px"
                            fontSize="md"
                            color="black"
                            fontFamily="'Plus Jakarta Sans', sans-serif"
                            _focus={{ borderColor: "#e5be54", boxShadow: "0 0 0 1px #e5be54" }}
                            bg="gray.50"
                          />
                        </Box>
                      </Flex>
                      <Flex justify="flex-end">
                        <Button
                          size="md"
                          color="white"
                          bg="#f64b4b"
                          px={6}
                          borderRadius="10px"
                          _hover={{ bg: "#d43d3d", transform: "translateY(-1px)" }}
                          _active={{ transform: "translateY(0)" }}
                          fontWeight="600"
                          fontSize="sm"
                          onClick={() => {
                            resetPrice();
                            onClose();
                          }}
                          transition="all 0.2s"
                        >
                          {intl.formatMessage({ id: "reset", defaultMessage: "Reset" })}
                        </Button>
                      </Flex>
                    </Flex>
                  </MenuList>
                </>)}
            </Menu>

            {selectedTab === "Commercial" && (
              <>
                <Box w="1px" h="50px" bg="gray.300" display={{ base: "none", md: "block" }} mx={2} />
                <Menu matchWidth={isMobile}>
                  {({ onClose }) => (
                    <>
                      <MenuButton
                        as={Button}
                        variant="unstyled"
                        flex={1}
                        height="auto"
                        px={4}
                        py={5}
                        width="100%"
                        borderRadius="lg"
                        transition="all 0.2s"
                        _hover={{ bg: "gray.100" }}
                      >
                        <Flex align="center" justify="space-between" width="100%">
                          <Text isTruncated fontSize="md" fontWeight="300" color="gray.600" textAlign="left">
                            {stateData.minSize || stateData.maxSize ? (
                              `${stateData.minSize || 0} - ${stateData.maxSize || intl.formatMessage({ id: "searchBar.any", defaultMessage: "Any" })} ${intl.formatMessage({ id: "searchBar.sqft_unit", defaultMessage: "sqft" })}`
                            ) : intl.formatMessage({ id: "searchBar.area_sqft", defaultMessage: "Area (sqft)" })}
                          </Text>
                          <ChevronDownIcon color="gray.400" ml={2} />
                        </Flex>
                      </MenuButton>
                      <MenuList p={{ base: 4, md: 6 }} borderRadius="16px" boxShadow="2xl" border="1px solid" borderColor="gray.100" zIndex={2000} minW={{ base: "0", md: "350px" }}>
                        <Flex direction="column" gap={6}>
                          <Flex align="center" gap={3}>
                            <Box flex={1}>
                              <Text mb={2} fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="1px">
                                {intl.formatMessage({ id: "searchBar.min_area", defaultMessage: "Min Area" })}
                              </Text>
                              <Input
                                placeholder="0"
                                value={stateData.minSize}
                                onChange={handleMinAreaChange}
                                h="45px"
                                borderRadius="12px"
                                fontSize="md"
                                color="black"
                                fontFamily="'Plus Jakarta Sans', sans-serif"
                                _focus={{ borderColor: "#e5be54", boxShadow: "0 0 0 1px #e5be54" }}
                                bg="gray.50"
                              />
                            </Box>
                            <Text mt={6} color="gray.400" fontWeight="300">—</Text>
                            <Box flex={1}>
                              <Text mb={2} fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="1px">
                                {intl.formatMessage({ id: "searchBar.max_area", defaultMessage: "Max Area" })}
                              </Text>
                              <Input
                                placeholder={intl.formatMessage({ id: "searchBar.any", defaultMessage: "Any" })}
                                value={stateData.maxSize}
                                onChange={handleMaxAreaChange}
                                h="45px"
                                borderRadius="12px"
                                fontSize="md"
                                color="black"
                                fontFamily="'Plus Jakarta Sans', sans-serif"
                                _focus={{ borderColor: "#e5be54", boxShadow: "0 0 0 1px #e5be54" }}
                                bg="gray.50"
                              />
                            </Box>
                          </Flex>
                          <Flex justify="flex-end">
                            <Button
                              size="md"
                              color="white"
                              bg="#f64b4b"
                              px={6}
                              borderRadius="10px"
                              _hover={{ bg: "#d43d3d", transform: "translateY(-1px)" }}
                              _active={{ transform: "translateY(0)" }}
                              fontWeight="600"
                              fontSize="sm"
                              onClick={() => {
                                resetArea();
                                onClose();
                              }}
                              transition="all 0.2s"
                            >
                              {intl.formatMessage({ id: "reset", defaultMessage: "Reset" })}
                            </Button>
                          </Flex>
                        </Flex>
                      </MenuList>
                    </>
                  )}
                </Menu>
              </>
            )}
          </Flex>

          <IconButton
            onClick={searchHandler}
            aria-label="Search"
            icon={<SearchIcon fontSize="22px" />}
            bg="#e5be54"
            color="white"
            borderRadius="full"
            width="70px"
            height="70px"
            flexShrink={0}
            _hover={{ bg: "#d4a93a", transform: "scale(1.05)", boxShadow: "0 10px 20px rgba(229, 190, 84, 0.3)" }}
            _active={{ transform: "scale(0.95)" }}
            boxShadow="0 4px 12px rgba(229, 190, 84, 0.2)"
          />
        </Flex>
      </Box>
    </Box>
  );
};

export default SearchBar;
